// src/components/auth/LoginForm.tsx
import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Google,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { useAuthStore } from "../../stores/authStore";
import { useT } from '../../i18n/useT';

interface LoginFormProps {
  onSuccess: () => void;
  onForgotPassword: () => void;
  onSwitchToSignup: () => void;
  isUpgrade?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onForgotPassword,
  onSwitchToSignup,
  isUpgrade = false,
}) => {
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // When signing in an existing account from an anonymous session,
  // the anonymous data on this device will be replaced. We confirm
  // before doing it. `pendingMethod` tracks which sign-in path the
  // user wants to take after confirming.
  const [pendingMethod, setPendingMethod] = useState<
    null | "email" | "google"
  >(null);

  const { signInWithEmail, signInWithGoogle, clearError } = useAuthStore();

  // The store flips authMode based on the live Firebase user. While
  // anonymous, isUpgrade will be true. We use that as the trigger
  // for the confirmation prompt — the caller passes it in.
  const needsDestructiveConfirm = isUpgrade;

  const performEmailSignIn = async () => {
    if (!email.trim() || !password.trim()) return;
    try {
      setIsSubmitting(true);
      clearError();
      await signInWithEmail(email.trim(), password);
      onSuccess();
    } catch {
      // store sets friendly error message
    } finally {
      setIsSubmitting(false);
    }
  };

  const performGoogleSignIn = async () => {
    try {
      setIsSubmitting(true);
      clearError();
      // Don't pass gdprConsent — that triggers the deferred-consent
      // path in the store. Returning users (with consent already on
      // file) sign in immediately and onSuccess closes the modal;
      // new users land on pendingGoogleConsent and the modal swaps
      // to the consent step rather than closing.
      await signInWithGoogle();
      // Keep the modal OPEN if any deferred Google flow is now
      // pending. Two cases hand control back to AuthModal instead
      // of closing:
      //   - pendingGoogleConsent: new Google user needs GDPR consent
      //   - pendingGoogleLink: email already has a password account;
      //     ask the user for that password and link Google to it
      // Closing here in either case would dismiss the dialog before
      // the user ever sees the next step (and without a recovery
      // route would show a white screen).
      const state = useAuthStore.getState();
      if (!state.pendingGoogleConsent && !state.pendingGoogleLink) {
        onSuccess();
      }
    } catch {
      // store sets friendly error message
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !password.trim()) return;
    if (needsDestructiveConfirm) {
      setPendingMethod("email");
      return;
    }
    await performEmailSignIn();
  };

  const handleGoogleLogin = async () => {
    if (needsDestructiveConfirm) {
      setPendingMethod("google");
      return;
    }
    await performGoogleSignIn();
  };

  const confirmDestructive = async () => {
    const method = pendingMethod;
    setPendingMethod(null);
    if (method === "email") await performEmailSignIn();
    else if (method === "google") await performGoogleSignIn();
  };

  const isFormValid = email.trim() && password.trim();

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isUpgrade
          ? t("Sign in with your existing account to upgrade and keep your conversations")
          : t("Welcome back! Please sign in to your account")}
      </Typography>

      <TextField
        fullWidth
        type="email"
        label={t("Email")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={isSubmitting}
        margin="normal"
        required
        autoComplete="email"
        autoFocus
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        type={showPassword ? "text" : "password"}
        label={t("Password")}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={isSubmitting}
        margin="normal"
        required
        autoComplete="current-password"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                disabled={isSubmitting}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ mt: 2, mb: 2 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={onForgotPassword}
          disabled={isSubmitting}
          sx={{ textDecoration: "none" }}
        >
          
          {t("Forgot your password?")}
        </Link>
      </Box>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={!isFormValid || isSubmitting}
        sx={{ mt: 2, mb: 2, py: 1.5 }}
      >
        {isSubmitting ? <CircularProgress size={24} /> : t("Sign In")}
      </Button>

      <Divider sx={{ my: 2 }}>
        <Typography variant="body2" color="text.secondary">
          
          {t("or")}
        </Typography>
      </Divider>

      <Button
        fullWidth
        variant="outlined"
        startIcon={<Google />}
        onClick={handleGoogleLogin}
        disabled={isSubmitting}
        sx={{ py: 1.5, mb: 2 }}
      >
        
        {t("Continue with Google")}
      </Button>

      {isUpgrade && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              
              {t("or")}
            </Typography>
          </Divider>

          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 2 }}
          >
            
            {t("Don't have an account yet?")}
          </Typography>

          <Button
            variant="outlined"
            fullWidth
            onClick={onSwitchToSignup}
            disabled={isSubmitting}
            sx={{ py: 1.5 }}
          >
            
            {t("Create New Account")}
          </Button>
        </>
      )}

      {!isUpgrade && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          
          {t("Don't have an account?")}{" "}
          <Link
            component="button"
            type="button"
            onClick={onSwitchToSignup}
            disabled={isSubmitting}
            sx={{ textDecoration: "none" }}
          >
            
            {t("Create one here")}
          </Link>
        </Typography>
      )}

      {/* Confirmation: signing into an existing account from an
          anonymous session replaces the anonymous data on this
          device. We make this explicit so the user doesn't lose
          conversations / postcards / pinned places by surprise. */}
      <Dialog
        open={pendingMethod !== null}
        onClose={() => setPendingMethod(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberOutlined color="warning" />
          
          {t("Replace this device's data?")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            
            {t("You're about to sign into an existing account. Anything you've done in this anonymous session — conversations, postcards, pinned places — won't carry over.")}
          </DialogContentText>
          <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
            
            {t("If you want to")} <strong>{t("save this session instead")}</strong>{t(", cancel and use \"Create account\" — it'll keep everything you've done so far.")}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendingMethod(null)} disabled={isSubmitting}>
            
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmDestructive}
            disabled={isSubmitting}
          >
            
            {t("Sign in anyway")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
